import { useEffect, useState, useCallback } from 'react'
import { apiStockList, apiStockHistory, apiStockBuy, apiStockSell } from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface StockRow {
  STOCK_CODE: number; STOCK_NAME: string; SECTOR: string
  CUR_PRICE:  number; PREV_PRICE: number; DIFF: number; DIFF_PCT: number
  HOLD_QTY:   number; HOLD_AVG:   number
}
interface HistoryRow { ROUND_NO: number; PRICE: number }

interface Props { auth: AuthState; myCash: number; nonce?: number }

const SECTOR_KO: Record<string, string> = {
  SPORTS:'스포츠', MEDIA:'미디어', FOOD:'식품', IT:'IT', FINANCE:'금융', ENERGY:'에너지'
}
const FEE_BPS = 30   // 매매 수수료 0.3%

// ── Phase 9: 30틱 히스토리에서 클라 계산 지표 ──────────────
interface Indicators { ma5: number | null; ma20: number | null; hi: number; lo: number; periodPct: number; vol: number; n: number }
function calcIndicators(h: HistoryRow[]): Indicators | null {
  if (h.length === 0) return null
  const p = h.map(x => x.PRICE)
  const n = p.length
  const ma = (k: number) => n >= k ? Math.round(p.slice(-k).reduce((a, b) => a + b, 0) / k) : null
  const hi = Math.max(...p), lo = Math.min(...p)
  const first = p[0], last = p[n - 1]
  const periodPct = first > 0 ? (last - first) / first * 100 : 0
  const mean = p.reduce((a, b) => a + b, 0) / n
  const variance = p.reduce((a, b) => a + (b - mean) ** 2, 0) / n
  const vol = mean > 0 ? Math.sqrt(variance) / mean * 100 : 0
  return { ma5: ma(5), ma20: ma(20), hi, lo, periodPct, vol, n }
}

export default function Stock({ auth, myCash, nonce }: Props) {
  const [rows, setRows] = useState<StockRow[]>([])
  const [sector, setSector] = useState<string>('')
  const [selected, setSelected] = useState<StockRow | null>(null)
  const [history, setHistory] = useState<HistoryRow[]>([])
  const [actionMsg, setActionMsg] = useState<{text: string, ok: boolean} | null>(null)
  const [loading, setLoading] = useState(true)
  const [trade, setTrade] = useState<{ stock: StockRow, action: 'buy' | 'sell' } | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    const r = await apiStockList(auth.token)
    if (r.resultCode === '00') {
      try { const obj = JSON.parse(r.resultData); setRows(obj.rows ?? []) } catch {}
    }
    setLoading(false)
  }, [auth.token])

  useEffect(() => { reload() }, [reload, nonce])

  const selectStock = useCallback(async (s: StockRow) => {
    setSelected(s)
    const r = await apiStockHistory(auth.token, s.STOCK_CODE)
    if (r.resultCode === '00') {
      try { const obj = JSON.parse(r.resultData); setHistory(obj.rows ?? []) } catch {}
    }
  }, [auth.token])

  async function confirmTrade(qty: number) {
    if (!trade) return
    const { stock, action } = trade
    const r = await (action === 'buy'
      ? apiStockBuy(auth.token, stock.STOCK_CODE, qty)
      : apiStockSell(auth.token, stock.STOCK_CODE, qty))
    setActionMsg({ text: r.resultMessage, ok: r.resultCode === '00' })
    setTrade(null)
    if (r.resultCode === '00') { reload(); if (selected) selectStock(selected) }
  }

  const filtered = sector === '' ? rows : rows.filter(r => r.SECTOR === sector)
  const myPortfolio = rows.filter(r => r.HOLD_QTY > 0)
  const portfolioValue = myPortfolio.reduce((s, r) => s + r.HOLD_QTY * r.CUR_PRICE, 0)
  const portfolioCost  = myPortfolio.reduce((s, r) => s + r.HOLD_QTY * r.HOLD_AVG, 0)
  const profit = portfolioValue - portfolioCost
  const profitPct = portfolioCost > 0 ? profit / portfolioCost * 100 : 0

  // ── 섹터 요약 (평균 등락률) ──
  const sectorStats = Object.keys(SECTOR_KO).map(key => {
    const list = rows.filter(r => r.SECTOR === key)
    const avg = list.length ? list.reduce((s, r) => s + r.DIFF_PCT, 0) / list.length : 0
    return { key, avg, count: list.length }
  }).filter(s => s.count > 0)

  const ind = selected ? calcIndicators(history) : null

  if (loading) return <div className="page-pad">주식 정보 불러오는 중…</div>

  return (
    <div className="page-pad stock-page">
      <h2>주식시장</h2>
      <section className="card stock-portfolio">
        <div><label>현금</label><strong>₩ {myCash.toLocaleString()}</strong></div>
        <div><label>평가액</label><strong>₩ {portfolioValue.toLocaleString()}</strong></div>
        <div><label>매입원가</label><strong>₩ {portfolioCost.toLocaleString()}</strong></div>
        <div><label>평가손익</label>
          <strong className={profit >= 0 ? 'profit' : 'loss'}>
            {profit >= 0 ? '+ ' : '− '}₩ {Math.abs(profit).toLocaleString()}
            {portfolioCost > 0 && <span className="pct"> ({profit >= 0 ? '+' : ''}{profitPct.toFixed(2)}%)</span>}
          </strong>
        </div>
      </section>

      {/* ── Phase 9: 섹터 요약 ── */}
      <section className="card stock-sectors">
        <span className="sectors-label">섹터 등락</span>
        {sectorStats.map(s => (
          <button
            key={s.key}
            className={`sector-chip ${sector === s.key ? 'on' : ''} ${s.avg >= 0 ? 'up' : 'down'}`}
            onClick={() => setSector(sector === s.key ? '' : s.key)}
          >
            {SECTOR_KO[s.key]} <b>{s.avg >= 0 ? '▲' : '▼'}{Math.abs(s.avg).toFixed(2)}%</b>
          </button>
        ))}
        {sector !== '' && <button className="sector-chip clear" onClick={() => setSector('')}>전체</button>}
      </section>

      {actionMsg && <p className={actionMsg.ok ? 'msg ok' : 'msg'}>{actionMsg.text}</p>}

      <div className="stock-2col">
        <section className="card">
          <div className="stock-list-head">
            <h3>{sector === '' ? '전체 종목' : `${SECTOR_KO[sector]} 종목`} ({filtered.length})</h3>
            <button onClick={reload}>새로고침</button>
          </div>
          <table className="data-table">
            <thead><tr><th>섹터</th><th>종목</th><th>현재가</th><th>등락</th><th>보유</th><th>평단</th><th>액션</th></tr></thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.STOCK_CODE} className={selected?.STOCK_CODE === s.STOCK_CODE ? 'sel' : ''} onClick={() => selectStock(s)}>
                  <td><span className={`sector-${s.SECTOR}`}>{SECTOR_KO[s.SECTOR]}</span></td>
                  <td>{s.STOCK_NAME}</td>
                  <td className="num">₩ {s.CUR_PRICE.toLocaleString()}</td>
                  <td className={`num ${s.DIFF >= 0 ? 'up' : 'down'}`}>
                    {s.DIFF >= 0 ? '▲' : '▼'} {Math.abs(s.DIFF).toLocaleString()} ({s.DIFF_PCT.toFixed(2)}%)
                  </td>
                  <td className="num">{s.HOLD_QTY}</td>
                  <td className="num">{s.HOLD_QTY > 0 ? `₩ ${s.HOLD_AVG.toLocaleString()}` : '-'}</td>
                  <td>
                    <button className="btn-buy"  onClick={(e) => { e.stopPropagation(); setTrade({ stock: s, action: 'buy' }) }}>매수</button>
                    {s.HOLD_QTY > 0 && <button className="btn-cancel" onClick={(e) => { e.stopPropagation(); setTrade({ stock: s, action: 'sell' }) }}>매도</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="card stock-detail">
          <h3>차트 · 지표</h3>
          {selected ? (
            <>
              <div className="detail-name">{selected.STOCK_NAME}</div>
              <div className="detail-meta">
                <span>{SECTOR_KO[selected.SECTOR]}</span> · <span>현재가 ₩{selected.CUR_PRICE.toLocaleString()}</span>
                <span className={selected.DIFF >= 0 ? 'up' : 'down'}> {selected.DIFF >= 0 ? '▲' : '▼'}{selected.DIFF_PCT.toFixed(2)}%</span>
              </div>
              <ChartSvg history={history} ma5={ind?.ma5 ?? null} />
              {ind && (
                <div className="stock-indicators">
                  <div><label>MA5</label><span>{ind.ma5 != null ? `₩${ind.ma5.toLocaleString()}` : '-'}</span></div>
                  <div><label>MA20</label><span>{ind.ma20 != null ? `₩${ind.ma20.toLocaleString()}` : '-'}</span></div>
                  <div><label>기간 고가</label><span>₩{ind.hi.toLocaleString()}</span></div>
                  <div><label>기간 저가</label><span>₩{ind.lo.toLocaleString()}</span></div>
                  <div><label>기간 등락</label><span className={ind.periodPct >= 0 ? 'up' : 'down'}>{ind.periodPct >= 0 ? '+' : ''}{ind.periodPct.toFixed(2)}%</span></div>
                  <div><label>변동성</label><span>{ind.vol.toFixed(2)}%</span></div>
                </div>
              )}
              <p className="muted indicator-note">최근 {ind?.n ?? 0}틱(라운드) 기준 · MA=이동평균 · 변동성=표준편차/평균</p>
            </>
          ) : <div className="detail-empty">종목 행을 클릭하면<br />차트와 지표가 표시됩니다.</div>}
        </section>
      </div>

      {trade && (
        <TradeModal
          stock={trade.stock}
          action={trade.action}
          myCash={myCash}
          onClose={() => setTrade(null)}
          onConfirm={confirmTrade}
        />
      )}
    </div>
  )
}

// ── Phase 9: 매수/매도 모달 (window.prompt 대체) ──────────────
function TradeModal({ stock, action, myCash, onClose, onConfirm }:
  { stock: StockRow, action: 'buy' | 'sell', myCash: number, onClose: () => void, onConfirm: (qty: number) => void }) {
  const isBuy = action === 'buy'
  const maxQty = isBuy ? Math.floor(myCash / Math.max(1, stock.CUR_PRICE)) : stock.HOLD_QTY
  const [qty, setQty] = useState<number>(Math.min(10, maxQty) || 1)

  const gross = qty * stock.CUR_PRICE
  const fee = Math.round(gross * FEE_BPS / 10000)
  const total = isBuy ? gross + fee : gross - fee
  const valid = qty > 0 && qty <= maxQty

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h3>{stock.STOCK_NAME} {isBuy ? '매수' : '매도'}</h3>
        <div className="modal-row"><label>현재가</label><span>₩ {stock.CUR_PRICE.toLocaleString()}</span></div>
        <div className="modal-row"><label>{isBuy ? '매수 가능' : '보유 수량'}</label><span>{maxQty.toLocaleString()}주</span></div>
        <div className="modal-qty">
          <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
          <input type="number" value={qty} min={1} max={maxQty}
            onChange={e => setQty(Math.max(0, Math.min(maxQty, Number(e.target.value) || 0)))} />
          <button onClick={() => setQty(q => Math.min(maxQty, q + 1))}>+</button>
          <button className="max-btn" onClick={() => setQty(maxQty)}>최대</button>
        </div>
        <div className="modal-row"><label>거래액</label><span>₩ {gross.toLocaleString()}</span></div>
        <div className="modal-row"><label>수수료 (0.3%)</label><span>₩ {fee.toLocaleString()}</span></div>
        <div className="modal-row total"><label>{isBuy ? '총 결제액' : '실 수령액'}</label><strong>₩ {total.toLocaleString()}</strong></div>
        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose}>취소</button>
          <button className={isBuy ? 'btn-buy' : 'btn-sell'} disabled={!valid} onClick={() => onConfirm(qty)}>
            {isBuy ? '매수' : '매도'} 확정
          </button>
        </div>
      </div>
    </div>
  )
}

function ChartSvg({ history, ma5 }: { history: HistoryRow[]; ma5: number | null }) {
  if (history.length < 2) return <p className="muted">시세 이력 부족 (라운드 진행 시 누적)</p>
  const w = 280, h = 160, pad = 12
  const minP = Math.min(...history.map(h => h.PRICE))
  const maxP = Math.max(...history.map(h => h.PRICE))
  const range = Math.max(1, maxP - minP)
  const dx = (w - pad * 2) / (history.length - 1)
  const yOf = (price: number) => h - pad - ((price - minP) / range) * (h - pad * 2)
  const points = history.map((p, i) => `${pad + i * dx},${yOf(p.PRICE)}`).join(' ')
  const last = history[history.length - 1].PRICE
  const first = history[0].PRICE
  const isUp = last >= first

  return (
    <svg width={w} height={h} className="stock-chart">
      <rect x="0" y="0" width={w} height={h} fill="#102234" />
      {/* 고가/저가 기준선 */}
      <line x1={pad} y1={yOf(maxP)} x2={w - pad} y2={yOf(maxP)} stroke="#2a3a5a" strokeWidth="1" strokeDasharray="3 3" />
      <line x1={pad} y1={yOf(minP)} x2={w - pad} y2={yOf(minP)} stroke="#2a3a5a" strokeWidth="1" strokeDasharray="3 3" />
      {/* MA5 기준선 */}
      {ma5 != null && ma5 >= minP && ma5 <= maxP && (
        <line x1={pad} y1={yOf(ma5)} x2={w - pad} y2={yOf(ma5)} stroke="#ffd54f" strokeWidth="1" strokeDasharray="5 3" />
      )}
      {/* 가격 라인 */}
      <polyline points={points} fill="none" stroke={isUp ? '#64ffda' : '#ff7043'} strokeWidth="2" />
      {/* 현재가 점 */}
      <circle cx={pad + (history.length - 1) * dx} cy={yOf(last)} r="3" fill={isUp ? '#64ffda' : '#ff7043'} />
      <text x={pad} y={11} fill="#8892b0" fontSize="10">고 {maxP.toLocaleString()}</text>
      <text x={pad} y={h - 3} fill="#8892b0" fontSize="10">저 {minP.toLocaleString()}</text>
      {ma5 != null && <text x={w - pad} y={11} fill="#ffd54f" fontSize="10" textAnchor="end">MA5</text>}
    </svg>
  )
}
